import React from "react";

/**
 * Sibling of InvoicePrintCard — same visual language (white card, print-
 * optimized), but generic across every Document Studio document type: it
 * renders `body_sections` (produced by Manus or a deterministic fallback,
 * both in the same [{heading, paragraphs}] shape) rather than an invoice's
 * line-items table.
 */
const DocumentPrintCard = ({ document, verificationUrl }) => {
  if (!document) return null;
  const issuer = document.issuer || {};
  const counterparty = document.counterparty;

  return (
    <article
      className="doc-print-area invoice-print-area bg-white text-[#222] rounded-lg shadow-2xl px-10 md:px-14 py-12"
      style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
    >
      {/* Header */}
      <header className="border-b-2 border-[#222] pb-5 mb-8">
        <h1 className="text-[26px] font-bold text-[#222] mb-1 uppercase tracking-wide">{document.title}</h1>
        <div className="text-[11px] text-[#999] mb-4">
          Document ID <span className="font-mono">{document.id.slice(0, 8)}</span>
          {document.jurisdiction && <> · Governing law: {document.jurisdiction}</>}
        </div>
        <div className="grid md:grid-cols-2 gap-10 text-[13px] text-[#555] leading-[1.6]">
          <div>
            <div className="text-[12px] font-bold text-[#222] mb-1">Issued By:</div>
            <div className="text-[14px] text-[#222]">{issuer.name}</div>
            <div className="text-[12px] text-[#777] mt-0.5">{issuer.company}</div>
            <div className="text-[12px] text-[#777]">{issuer.email}</div>
          </div>
          {counterparty && (counterparty.name || counterparty.company) && (
            <div>
              <div className="text-[12px] font-bold text-[#222] mb-1">Counterparty:</div>
              <div className="text-[14px] text-[#222]">{counterparty.name}</div>
              {counterparty.company && <div className="text-[12px] text-[#777] mt-0.5">{counterparty.company}</div>}
              {counterparty.email && <div className="text-[12px] text-[#777]">{counterparty.email}</div>}
              {counterparty.address && <div className="text-[12px] text-[#777]">{counterparty.address}</div>}
            </div>
          )}
        </div>
      </header>

      {/* Body sections */}
      <div className="space-y-6 mb-8">
        {document.body_sections?.map((s, i) => (
          <section key={i} style={{ breakInside: "avoid" }}>
            <h2 className="text-[14px] font-bold text-[#222] mb-2">
              {i + 1}. {s.heading}
            </h2>
            {s.paragraphs?.map((p, j) => (
              <p key={j} className="text-[13px] text-[#333] leading-[1.75] mb-2">{p}</p>
            ))}
          </section>
        ))}
        {(!document.body_sections || document.body_sections.length === 0) && (
          <p className="text-[13px] text-[#999] italic">Drafting in progress…</p>
        )}
      </div>

      {/* Signature block — only for documents with a named counterparty */}
      {counterparty && counterparty.name && (
        <section className="grid md:grid-cols-2 gap-10 mb-8 pt-6 border-t border-[#eee]" style={{ breakInside: "avoid" }}>
          <div>
            <div className="border-b border-[#333] h-10 mb-1.5" />
            <div className="text-[11px] text-[#777]">Signature — {issuer.company || issuer.name}</div>
            <div className="text-[11px] text-[#999] mt-3">Date: _______________</div>
          </div>
          <div>
            <div className="border-b border-[#333] h-10 mb-1.5" />
            <div className="text-[11px] text-[#777]">Signature — {counterparty.name}</div>
            <div className="text-[11px] text-[#999] mt-3">Date: _______________</div>
          </div>
        </section>
      )}

      {/* Disclaimer */}
      {document.disclaimer && (
        <section className="bg-[#f8f8f8] border-l-[3px] border-[#d4af37] px-5 py-4 mb-6 text-[11px] text-[#555] leading-[1.6]">
          {document.disclaimer}
        </section>
      )}

      {/* Verification */}
      {verificationUrl && document.public_verify && (
        <section className="border-t border-[#eee] pt-4 mb-4 text-[10.5px] text-[#888]">
          <strong>Verify:</strong> <a className="underline" href={verificationUrl}>{verificationUrl}</a>
        </section>
      )}

      <footer className="border-t border-[#eee] pt-3 text-[11px] text-[#888] text-center">
        <p>© {new Date().getFullYear()} {issuer.company} | {issuer.name}</p>
        <p className="mt-1 text-[#bbb] font-mono uppercase tracking-[0.18em] text-[9px]">
          {document.body_source === "manus" ? "Drafted by AYI_all · Manus" : "Generated from standard template"}
        </p>
      </footer>
    </article>
  );
};

export default DocumentPrintCard;
